import { h, JSX } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { TextField } from './TextField';
import { CloseIcon } from './Icons';
import { CardType } from '../../types/types';
import { formatPrice } from '../number-utils';

declare global {
    interface Window {
        membershipEmailTimeout?: ReturnType<typeof setTimeout>;
    }
}

export interface MembershipPurchaseModalProps {
    show: boolean;
    toggleModal: () => void;
    cardType: CardType | null;
    lang: {
        title: string;
        emailLabel: string;
        emailPlaceholder: string;
        nameLabel: string;
        namePlaceholder: string;
        existingMemberNote: string;
        addToCartLabel: string;
        cancelLabel: string;
        searchingLabel: string;
        monthsLabel: string;
    };
    currencySymbol: string;
    searchMemberUrl: string;
    /** Resolves to the server's message when it turns the membership back, such as for an address it would refuse. */
    onAddToCart: (data: {
        cardTypeId: number;
        email?: string;
        name?: string;
        memberId?: number;
    }) => Promise<string | undefined>;
}

interface MemberSearchResult {
    id: number;
    name: string;
    email: string;
    cards: {
        id: number;
        number: string;
        type: string;
        valid_to: string;
        is_active: boolean;
    }[];
}

export const MembershipPurchaseModal = (props: MembershipPurchaseModalProps): JSX.Element => {
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [name, setName] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [existingMember, setExistingMember] = useState<MemberSearchResult | null>(null);

    useEffect(() => {
        if (!props.show) {
            setEmail('');
            setEmailError('');
            setName('');
            setExistingMember(null);
        }
    }, [props.show]);

    const searchMember = async (emailValue: string) => {
        if (!emailValue || isSearching) {
            return;
        }

        setIsSearching(true);
        try {
            const response = await fetch(`${props.searchMemberUrl}?q=${encodeURIComponent(emailValue)}`);
            const data = await response.json();

            if (data.data && data.data.length > 0) {
                const member = data.data[0];
                // Only set existing member if the email matches exactly
                if (member.email && member.email.toLowerCase() === emailValue.toLowerCase()) {
                    setExistingMember(member);
                    setName(member.name || '');
                } else {
                    setExistingMember(null);
                }
            } else {
                setExistingMember(null);
            }
        } catch (error) {
            console.error('Error searching member:', error);
            setExistingMember(null);
        } finally {
            setIsSearching(false);
        }
    };

    const handleAddToCart = async () => {
        if (!props.cardType || isAdding) {
            return;
        }

        const data: { cardTypeId: number; email?: string; name?: string; memberId?: number } = {
            cardTypeId: props.cardType.id,
        };

        if (email) {
            data.email = email;
        }
        if (name) {
            data.name = name;
        }
        if (existingMember) {
            data.memberId = existingMember.id;
        }

        setIsAdding(true);
        const refusal = await props.onAddToCart(data);
        setIsAdding(false);

        if (refusal) {
            setEmailError(refusal);
            return;
        }

        props.toggleModal();
    };

    if (!props.cardType) {
        return <div />;
    }

    return (
        <div
            className={
                'fixed inset-0 z-20 bg-black/60 whitespace-normal text-left flex flex-col p-4 md:p-8 lg:p-12' +
                (props.show ? '' : ' hidden')
            }
        >
            <div class="bg-white w-full max-w-[480px] m-auto rounded-lg relative overflow-auto">
                <div
                    class="absolute right-1 top-1 md:right-5 md:top-3.5 p-2 cursor-pointer rounded-full transition hover:bg-neutral-100 z-10"
                    onClick={props.toggleModal}
                >
                    <CloseIcon aria-hidden="true" />
                </div>
                <div class="flex flex-col">
                    <div class="flex flex-col p-8 grow min-h-[300px]">
                        {/* Add empty space at top to avoid X button overlap */}
                        <div class="h-8"></div>

                        {/* Membership info */}
                        <div class="p-4 bg-gray-50 rounded-lg mb-6">
                            <div class="flex justify-between items-center">
                                <span class="font-semibold">{props.cardType.name}</span>
                                <span class="font-semibold">
                                    {formatPrice(props.cardType.membership_price, props.currencySymbol)}
                                </span>
                            </div>
                            <div class="text-sm text-gray-600 mt-1">
                                {props.cardType.membership_duration_months} {props.lang.monthsLabel}
                            </div>
                        </div>

                        {/* Email field */}
                        <div class="mb-4">
                            <TextField
                                label={props.lang.emailLabel + (isSearching ? ' ...' : '')}
                                type="email"
                                value={email}
                                onChange={(value) => {
                                    setEmail(value);
                                    setEmailError('');
                                    // Clear any existing timeout
                                    if (window.membershipEmailTimeout) {
                                        clearTimeout(window.membershipEmailTimeout);
                                    }
                                    // Set new timeout for search
                                    window.membershipEmailTimeout = setTimeout(() => {
                                        if (value) {
                                            searchMember(value);
                                        }
                                    }, 500);
                                }}
                                placeholder={props.lang.emailPlaceholder}
                                disabled={isSearching}
                                error={emailError}
                            />
                        </div>

                        {/* Name field */}
                        <div class="mb-4">
                            <TextField
                                label={props.lang.nameLabel}
                                value={name}
                                onChange={setName}
                                placeholder={props.lang.namePlaceholder}
                                disabled={isSearching}
                            />
                        </div>

                        {/* Existing member note */}
                        {existingMember && (
                            <div class="mt-2 p-4 bg-blue-50 rounded-lg">
                                <p class="text-sm text-blue-800">{props.lang.existingMemberNote}</p>
                                {existingMember.cards.length > 0 && (
                                    <div class="mt-2 text-sm">
                                        {existingMember.cards.map((card) => (
                                            <div key={card.id} class="mt-1">
                                                {card.type}: {card.number}
                                                {card.is_active ? ' (Active)' : ' (Expired)'}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer buttons */}
                    <div class="flex shrink justify-end items-center border-t px-8 py-6">
                        <div class="flex gap-2.5">
                            <button onClick={props.toggleModal} class="btn btn-border p-4 text-base leading-[14px]">
                                {props.lang.cancelLabel}
                            </button>
                            <button
                                onClick={handleAddToCart}
                                class="btn btn-light-blue p-4 text-base leading-[14px]"
                                disabled={isSearching || isAdding}
                            >
                                {props.lang.addToCartLabel}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
